import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { Interval } from '@ui/constants/poll';
import { GenericTradeParams, TpSlValueState } from '@ui/features/Trade/types';
import { useDataChainId } from '@ui/hooks/datachain';
import { toReadableValue } from '@ui/lib/bignumber';
import { getLimitOrderFacet } from '@ui/services/contract/contract-instances/getLimitOrderFacet';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import { useUpdateOrderTpAndSlParams } from './useUpdateOrderTpAndSlParams';

export const useEstOrderEditTpSlGasLimit = <TSelect = BigInt>({
  genericTradeParams,
  tp,
  sl,
  opts,
}: {
  tp: TpSlValueState;
  sl: TpSlValueState;
  opts?: Pick<UseQueryOptions<BigInt, Error, TSelect>, 'enabled'>;
} & GenericTradeParams) => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const updateOrderTpAndSlParams = useUpdateOrderTpAndSlParams({
    genericTradeParams,
    tp,
    sl,
  });
  const LimitOrderFacet = useMemo(() => {
    if (!client) return;

    return getLimitOrderFacet({
      chainId,
      publicClient: client,
    });
  }, [chainId, client]);

  return useQuery({
    meta: {
      type: 'estimateGas',
    },
    queryKey: [
      'LimitOrderFacet.estimate.updateOrderTpAndSl',
      address,
      chainId,
      client?.uid,
      updateOrderTpAndSlParams,
    ],
    queryFn: async () => {
      if (isNil(updateOrderTpAndSlParams) || isNil(address)) return undefined;

      const re = await LimitOrderFacet?.estimateGas.updateOrderTpAndSl(
        [...updateOrderTpAndSlParams],
        {
          account: address,
        },
      );

      if (isNil(re)) return undefined;

      return toReadableValue(re);
    },
    refetchInterval: Interval.SLOW,
    enabled:
      !!address &&
      !!updateOrderTpAndSlParams &&
      !!LimitOrderFacet &&
      (opts?.enabled ?? true),
  });
};
